import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  AdministrationLayout,
  Badge,
  CardActions,
  Cluster,
  DialogActions,
  Form,
  FormField,
  FormSubmit,
  Grid,
  HeaderActions,
  Inline,
  Input,
  LoadMorePager,
  RowActions,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Section,
  Select,
  Skeleton,
  Surface,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ToolbarActions,
  useZodForm,
} from '../components/ui';
import type { ActionDescriptor } from '../components/ui';
import { AgentPanel } from '../components/agent/AgentPanel';
import { useAuth } from '../lib/auth';
import { useCursorList } from '../lib/query/useCursorList';
import {
  listOrgs,
  getOrg,
  createOrg,
  setOrgFlag,
  clearOrgFlag,
  assignOrgPlan,
  listFeatures,
  listPlans,
  updatePlan,
  revertPlanField,
  platformKeys,
  CreateOrgBodySchema,
  type Org,
  type Feature,
  type FlagSetState,
  type PlatformPlan,
  type UpdatePlanBody,
} from '../lib/api/platform.gen';
import { getPlanComparison, plansKeys } from '../lib/api/plans.gen';
import {
  listFeatureRequests,
  resolveFeatureRequest,
  featureRequestsKeys,
  type FeatureRequestQueueItem,
} from '../lib/api/featureRequests.gen';
import { explainPolicy, ExplainQuerySchema, type TraceRow, type PolicyExplainResult } from '../lib/api/policy.gen';

// Platform console (owner plane, story 1C-4, P01 §4.1 / P02 §6). Org table +
// per-org FlagMatrix — the owner sets each feature on / locked (teaser upsell,
// story 2-9) / off per org, including beyond plan with a note. A change bumps
// the org's flagVersion + pushes governance SSE, so the org's users see the
// feature unlock (or the teaser) without reload.

type ApiError = { response?: { data?: { message?: string; reason?: string } }; message?: string };
const messageOf = (e: unknown): string => {
  const err = e as ApiError;
  return err.response?.data?.message || err.message || 'Something went wrong.';
};

// G-26 (story 2-95b): composed over the generated contract schema — never
// hand-declared. The dialog only ever sets these 3 fields (`slug`/`ownerName`
// aren't exposed here), matching this page's existing `createOrgMutation`
// call shape (`{ name, ownerEmail, planSlug? }`).
const createOrgFormSchema = CreateOrgBodySchema.pick({ name: true, ownerEmail: true, planSlug: true });

export function PlatformConsolePage(): JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Create-organization modal (story 2-5, W1 step 1). G-26 (story 2-95b): a
  // real `Form`/`useZodForm` composition over `createOrgFormSchema`, not
  // `useState` fields.
  const [createOpen, setCreateOpen] = useState(false);
  const createOrgForm = useZodForm(createOrgFormSchema, { defaultValues: { name: '', ownerEmail: '', planSlug: '' } });

  // Policy inspector (story 2-51, P18 §11): "why can't Alice do X?" dry-run
  // EXPLAIN. G-26: the form is a real `Form`/`useZodForm` composition over
  // the GENERATED `ExplainQuerySchema` (`lib/api/policy.gen.ts`) — never a
  // hand-declared schema. `principalMode`/`targetMode` are plain UI state
  // (which of the schema's mutually-exclusive fields is currently shown),
  // not validation rules — the backend's own exactly-one-of check
  // (`policyExplain.service.js`) is the one source of truth for that, since
  // the generated query schema can't express it (see contracts/policy.js's
  // own comment on why: no contract puts `.refine()` on a `query`).
  const [principalMode, setPrincipalMode] = useState<'userId' | 'email'>('email');
  const [targetMode, setTargetMode] = useState<'capability' | 'featureKey' | 'metric'>('capability');
  const explainForm = useZodForm(ExplainQuerySchema, { defaultValues: { orgId: '', capability: '' } });
  const [explainResult, setExplainResult] = useState<PolicyExplainResult | null>(null);
  const explainMutation = useMutation({
    mutationFn: explainPolicy,
    onSuccess: (res) => setExplainResult(res.data),
    onError: (e) => { setExplainResult(null); setError(messageOf(e)); },
  });
  const QUOTA_METRIC_OPTIONS = ['reportsPerMonth', 'pdfExportsPerMonth', 'agentCallsPerMonth', 'templates', 'seats'] as const;
  const TRACE_VERDICT_TONE: Record<TraceRow['verdict'], 'success' | 'destructive' | 'neutral' | 'warning'> = {
    allow: 'success',
    deny: 'destructive',
    not_applicable: 'neutral',
    info: 'warning',
  };

  const isOwner = user?.platformRole === 'owner';

  // Story 2-72e: off the inline `['platform','orgs']` key onto the generated
  // `platformKeys` factory (G-19) + `useCursorList` (G-18), now that
  // `GET /platform/orgs` answers the shared cursor envelope instead of every
  // org on the platform, unfiltered and unpaged, in one response. Still
  // deliberately platform-wide (not narrowed to any one org — see
  // `contracts/platform.js`'s header) — the migration is pagination, not a
  // new tenant scope.
  const orgsList = useCursorList<Org>({
    queryKey: platformKeys.list({ scope: 'orgs' }),
    fetchPage: (pageParams) => listOrgs(pageParams),
    enabled: isOwner,
  });
  const featuresQuery = useQuery({
    queryKey: platformKeys.list({ scope: 'features' }),
    queryFn: () => listFeatures().then((r) => r.data),
    enabled: isOwner,
  });
  // Generated `platformKeys` factory (G-19) — `.detail(selectedOrgId)`
  // already puts the browsed-org dimension in the cache identity, which is
  // exactly what keeps this cross-org owner-plane read from ever serving one
  // org's detail while another is selected (story 2-73's org-switch scoping
  // decision: see docs/prd/audits/2-73.audit.json — this IS the platform
  // console's real analog of "organization switch").
  const orgDetailQuery = useQuery({
    queryKey: platformKeys.detail(selectedOrgId ?? 'none'),
    queryFn: () => getOrg(selectedOrgId as string).then((r) => r.data),
    enabled: isOwner && Boolean(selectedOrgId),
  });

  // Story 2-72e: same migration as `orgsList` above, for the pending
  // feature-request queue — also platform-wide (every org's pending
  // requests, not one org's), `q`ueue ordered oldest-first server-side.
  const requestsList = useCursorList<FeatureRequestQueueItem>({
    queryKey: featureRequestsKeys.list({ scope: 'pending' }),
    fetchPage: (pageParams) => listFeatureRequests(pageParams),
    enabled: isOwner,
  });

  const resolveMutation = useMutation({
    mutationFn: (vars: { id: string; grant: boolean }) => resolveFeatureRequest(vars.id, { grant: vars.grant }),
    onSuccess: async () => {
      // Resolving lives on its own module key (`featureRequestsKeys`), not
      // nested under `platformKeys` — both need invalidating: a grant also
      // flips an org flag (`platform.setOrgFlag`, reachable through
      // `platformKeys`'s prefix), and the resolved request must drop out of
      // the pending queue either way.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.all }),
        queryClient.invalidateQueries({ queryKey: featureRequestsKeys.all }),
      ]);
    },
    onError: (e) => setError(messageOf(e)),
  });

  // Same `GET /plans/compare` resource `PlanCompare` reads — shares its
  // `plansKeys.detail('compare')` cache entry (G-19) rather than a
  // platform-prefixed literal, so the two never disagree about this data.
  const plansQuery = useQuery({
    queryKey: plansKeys.detail('compare'),
    queryFn: () => getPlanComparison().then((r) => r.plans),
    enabled: isOwner,
  });

  // Plan editor (story 2-19, P02 §8): tier definitions as owner-editable records
  // — quota caps (-1 = unlimited), entitlement featureKeys, price. Edits take
  // effect on the org's next capability/quota resolve; no deploy or restart.
  const planEditorQuery = useQuery({
    queryKey: platformKeys.list({ scope: 'plan-editor' }),
    queryFn: () => listPlans(),
    enabled: isOwner,
  });
  // Numeric cell drafts keyed `${slug}:${metric}` (price uses metric 'price');
  // committed on blur/Enter so each keystroke doesn't fire a PATCH.
  const [limitDrafts, setLimitDrafts] = useState<Record<string, string>>({});

  const planMutation = useMutation({
    mutationFn: (vars: { slug: string; body: UpdatePlanBody }) => updatePlan(vars.slug, vars.body),
    onSuccess: async () => {
      setError(null);
      // Story 2-73 (A2): this write lands on the SAME plan data `PlanCompare`
      // and `PricingPage` cache under `plansKeys.detail('compare')` — before
      // this fix only `platformKeys.all` was invalidated, so an edit here
      // left every other surface reading the stale compare matrix until its
      // own `staleTime` expired (no stale-page-reuse regression, just a
      // missed invalidation this story's A2 sweep caught).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.all }),
        queryClient.invalidateQueries({ queryKey: plansKeys.all }),
      ]);
    },
    onError: (e) => setError(messageOf(e)),
  });

  // Revert a plan field to its code default / re-seed a drifted cap (story 2-31 c/d).
  const planRevertMutation = useMutation({
    mutationFn: (vars: { slug: string; field: string }) => revertPlanField(vars.slug, { field: vars.field }),
    onSuccess: async () => {
      setError(null);
      // Same cross-surface invalidation as `planMutation` above.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.all }),
        queryClient.invalidateQueries({ queryKey: plansKeys.all }),
      ]);
    },
    onError: (e) => setError(messageOf(e)),
  });

  const createOrgMutation = useMutation({
    mutationFn: (body: { name: string; ownerEmail: string; planSlug?: string }) => createOrg(body),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: platformKeys.all });
      setCreateOpen(false);
      createOrgForm.reset({ name: '', ownerEmail: '', planSlug: '' });
    },
    onError: (e) => setError(messageOf(e)),
  });

  const requests: FeatureRequestQueueItem[] = requestsList.items;

  const flagMutation = useMutation({
    mutationFn: (vars: { orgId: string; featureKey: string; state: FlagSetState; note?: string }) =>
      setOrgFlag(vars.orgId, { featureKey: vars.featureKey, state: vars.state, note: vars.note ?? null }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: platformKeys.all });
    },
    onError: (e) => setError(messageOf(e)),
  });

  // Clear a per-org override (story 2-31 part a): remove the override row so the
  // feature re-derives from plan/defaults — distinct from setting it 'off'.
  const clearFlagMutation = useMutation({
    mutationFn: (vars: { orgId: string; featureKey: string }) => clearOrgFlag(vars.orgId, vars.featureKey),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: platformKeys.all });
    },
    onError: (e) => setError(messageOf(e)),
  });

  // Plan assign (story 2-22, P02 §8): flip the selected org's subscription tier
  // live — entitlements + quotas re-resolve, flagVersion bumps + governance SSE
  // pushes, so the org's users see features flip (or the downgrade teaser)
  // without reload. A Stripe-billed org gets a local override + honest warning.
  const [assignWarning, setAssignWarning] = useState<string | null>(null);
  const assignPlanMutation = useMutation({
    mutationFn: (vars: { orgId: string; planSlug: string }) => assignOrgPlan(vars.orgId, { planSlug: vars.planSlug }),
    onSuccess: async (res) => {
      setError(null);
      setAssignWarning(res.warning);
      await queryClient.invalidateQueries({ queryKey: platformKeys.all });
    },
    onError: (e) => setError(messageOf(e)),
  });

  const orgs: Org[] = orgsList.items;
  const detail = orgDetailQuery.data;

  const groupedFeatures = useMemo(() => {
    const groups: Record<string, Feature[]> = {};
    (featuresQuery.data ?? []).forEach((f) => {
      (groups[f.category] = groups[f.category] ?? []).push(f);
    });
    return Object.entries(groups);
  }, [featuresQuery.data]);

  // Redirect non-owners once auth has loaded.
  if (user && !isOwner) return <Navigate to="/app" replace />;

  // Tri-state (story 2-9, P02 §5/§6): on = enabled, locked = discoverable
  // teaser/upsell, off = hidden. With no explicit flag row, an entitled feature
  // follows its default and a non-entitled one is plan-locked (teaser already).
  const stateFor = (feature: Feature): { state: FlagSetState; explicit: boolean; entitled: boolean } => {
    const entitled = Boolean(detail && detail.entitlements.includes(feature.key));
    const flag = detail?.flags.find((f) => f.featureKey === feature.key);
    if (flag) return { state: flag.state, explicit: true, entitled };
    if (entitled) return { state: feature.defaultForNewOrgs ? 'on' : 'off', explicit: false, entitled };
    return { state: 'locked', explicit: false, entitled };
  };

  const setState = (feature: Feature, next: FlagSetState) => {
    if (!detail) return;
    const { state, entitled } = stateFor(feature);
    if (next === state) return;
    let note: string | undefined;
    if (next === 'on' && !entitled) {
      // Grant beyond plan requires a note (P02 §6).
      // eslint-disable-next-line no-alert
      const entered = window.prompt(`Grant "${feature.name}" beyond plan — reason (required):`, '');
      if (!entered || !entered.trim()) return;
      note = entered.trim();
    }
    flagMutation.mutate({ orgId: detail.id, featureKey: feature.key, state: next, ...(note ? { note } : {}) });
  };

  // --- Plan editor helpers (story 2-19) -----------------------------------
  const editorPlans: PlatformPlan[] = planEditorQuery.data?.data ?? [];
  const editorMetrics: string[] = planEditorQuery.data?.metrics ?? [];

  const draftKey = (slug: string, field: string): string => `${slug}:${field}`;

  const setDraft = (key: string, value: string): void => setLimitDrafts((d) => ({ ...d, [key]: value }));
  const clearDraft = (key: string): void =>
    setLimitDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });

  const limitValue = (plan: PlatformPlan, metric: string): string => {
    const draft = limitDrafts[draftKey(plan.slug, metric)];
    if (draft !== undefined) return draft;
    const cap = plan.limits[metric];
    return cap === undefined ? '' : String(cap);
  };

  // Commit a numeric cap on blur/Enter. Blank leaves the metric uncapped —
  // absent metric = no cap configured; -1 is the explicit unlimited sentinel.
  const commitLimit = (plan: PlatformPlan, metric: string): void => {
    const key = draftKey(plan.slug, metric);
    const draft = limitDrafts[key];
    if (draft === undefined) return;
    clearDraft(key);
    if (draft.trim() === '') return;
    const value = Number(draft);
    if (!Number.isInteger(value) || value < -1 || value === plan.limits[metric]) return;
    planMutation.mutate({ slug: plan.slug, body: { limits: { [metric]: value } } });
  };

  const priceValue = (plan: PlatformPlan): string => {
    const draft = limitDrafts[draftKey(plan.slug, 'price')];
    if (draft !== undefined) return draft;
    return plan.priceMonthly === null ? '' : String(plan.priceMonthly);
  };

  const commitPrice = (plan: PlatformPlan): void => {
    const key = draftKey(plan.slug, 'price');
    const draft = limitDrafts[key];
    if (draft === undefined) return;
    clearDraft(key);
    const value = draft.trim() === '' ? null : Number(draft);
    if (value !== null && (!Number.isFinite(value) || value < 0)) return;
    if (value === plan.priceMonthly) return;
    planMutation.mutate({ slug: plan.slug, body: { priceMonthly: value } });
  };

  const toggleEntitlement = (plan: PlatformPlan, featureKey: string): void => {
    const entitled = plan.entitlements.includes(featureKey);
    const next = entitled ? plan.entitlements.filter((k) => k !== featureKey) : [...plan.entitlements, featureKey];
    planMutation.mutate({ slug: plan.slug, body: { entitlements: next } });
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const FLAG_STATES: Array<{ value: FlagSetState; label: string; title: string }> = [
    { value: 'on', label: 'On', title: 'Enabled for the org' },
    { value: 'locked', label: 'Teaser', title: 'Locked but discoverable — renders the upsell teaser' },
    { value: 'off', label: 'Off', title: 'Hidden from the org' },
  ];

  return (
    <AdministrationLayout
      header={{
        title: 'Platform console',
        description: 'Organizations across DefectSnap, and the features each one has.',
        actions: (
          <HeaderActions
            actions={[
              {
                id: 'create-organization',
                label: 'Create organization',
                variant: 'primary',
                onClick: () => { setError(null); setCreateOpen(true); },
              },
            ]}
          />
        ),
      }}
    >

      {error && <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}

      {createOpen && (
        <Modal label="Create an organization" onClose={() => setCreateOpen(false)}>
          <ModalHeader title="Create an organization" onClose={() => setCreateOpen(false)}>
            <p className="text-sm text-muted-foreground">
              We’ll set it up and email the superadmin an invite to set their password.
            </p>
          </ModalHeader>
          <ModalBody>
          <Form
            form={createOrgForm}
            id="create-org-form"
            className="space-y-4"
            onSubmit={(values) => {
              const name = values.name.trim();
              const email = values.ownerEmail.trim();
              createOrgMutation.mutate({ name, ownerEmail: email, ...(values.planSlug ? { planSlug: values.planSlug } : {}) });
            }}
          >
            <FormField name="name" label="Organization name" required>
              {(field) => (
                <Input className="w-full text-sm" placeholder="Acme Inspections" {...field} />
              )}
            </FormField>
            <FormField name="ownerEmail" label="Superadmin email" required>
              {(field) => (
                <Input type="email" autoComplete="off" className="w-full text-sm" placeholder="owner@acme.com" {...field} />
              )}
            </FormField>
            <FormField name="planSlug" label="Plan">
              {(field) => (
                <Select className="w-full text-sm" {...field}>
                  <option value="">Default (trial)</option>
                  {(plansQuery.data ?? []).map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name}</option>
                  ))}
                </Select>
              )}
            </FormField>
            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          </Form>
          </ModalBody>
          <ModalFooter>
            <DialogActions
              size="sm"
              actions={[
                { id: 'cancel', label: 'Cancel', variant: 'ghost', onClick: () => setCreateOpen(false) },
                {
                  id: 'create',
                  label: createOrgMutation.isPending ? 'Creating…' : 'Create & invite',
                  variant: 'primary',
                  htmlType: 'submit',
                  form: 'create-org-form',
                  disabled: createOrgMutation.isPending || !createOrgForm.watch('name')?.trim() || !createOrgForm.watch('ownerEmail')?.trim(),
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}

      {requests.length > 0 && (
        <Surface as="section" variant="flat" padding="xl" className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Feature requests</h2>
            <p className="text-sm text-muted-foreground">Members have asked for these locked features. Grant to enable for their org.</p>
          </div>
          <div className="space-y-2">
            {requests.map((req) => (
              <Cluster justify="between" gap={3} key={req.id} className="rounded-lg border border-border/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {req.featureName ?? req.featureKey} · <span className="text-muted-foreground">{req.organizationName ?? req.organizationId}</span>
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    Requested by {req.requestedByName || 'a member'}
                    {req.note ? ` — “${req.note}”` : ''}
                  </p>
                </div>
                <CardActions
                  actions={[
                    {
                      id: 'decline',
                      label: 'Decline',
                      variant: 'secondary',
                      disabled: resolveMutation.isPending,
                      onClick: () => resolveMutation.mutate({ id: req.id, grant: false }),
                    },
                    {
                      id: 'grant',
                      label: 'Grant',
                      variant: 'primary',
                      disabled: resolveMutation.isPending,
                      onClick: () => resolveMutation.mutate({ id: req.id, grant: true }),
                    },
                  ]}
                />
              </Cluster>
            ))}
          </div>
          {/* Story 2-72e: the queue is now a cursor page too — platform-wide
              (every org's pending requests), same `LoadMorePager` convention
              as the org table below. */}
          <LoadMorePager
            shownCount={requests.length}
            total={requestsList.total}
            hasMore={requestsList.hasMore}
            onLoadMore={requestsList.fetchNextPage}
            isLoadingMore={requestsList.isLoadingMore}
            data-testid="platform-feature-requests-count"
          />
        </Surface>
      )}

      {orgsList.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
        <Table aria-label="Organizations">
          <THead>
            <TR>
              <TH>Organization</TH>
              <TH>Plan</TH>
              <TH>Members</TH>
              <TH>Flags</TH>
              <TH> </TH>
            </TR>
          </THead>
          <TBody>
            {orgs.map((org) => (
              <TR key={org.id} className={clsx(org.id === selectedOrgId && 'bg-muted/40')}>
                {/* 4-100: Organization is the identity column — a genuinely long org
                    name forces the whole table into horizontal scroll without a
                    bounded width; `truncate` + `title` cap it in place. */}
                <TD truncate title={org.name}>
                  <span className="font-medium text-foreground">{org.name}</span>
                </TD>
                <TD>
                  <Badge tone="neutral">{org.plan}</Badge>
                </TD>
                <TD className="text-muted-foreground">{org.memberCount}</TD>
                <TD className="text-muted-foreground">{org.flagCount}</TD>
                <TD>
                  <RowActions
                    actions={[
                      {
                        id: 'configure-flags',
                        label: 'Configure flags',
                        variant: 'ghost',
                        onClick: () => { setAssignWarning(null); setSelectedOrgId(org.id); },
                      },
                    ]}
                    className="justify-end"
                  />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {/* Story 2-72e: the org table is now a cursor page (default 50), not
            every org on the platform in one unbounded response. The canonical
            `LoadMorePager` (story 2-147tb) — same component `ItemsListPage`
            converges onto — rather than a hand-rolled affordance. */}
        <LoadMorePager
          shownCount={orgs.length}
          total={orgsList.total}
          hasMore={orgsList.hasMore}
          onLoadMore={orgsList.fetchNextPage}
          isLoadingMore={orgsList.isLoadingMore}
          data-testid="platform-orgs-count"
        />
        </>
      )}

      <Section
        gap={5}
        title="Plans"
        description="Tier definitions are records — an edit applies on each org’s next resolve, no deploy. Limits are per monthly period; -1 means unlimited, blank means no cap. Ticks mark the features a tier is entitled to."
      >
        {planEditorQuery.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            {/* Plan drift (story 2-31 d): values that diverge from the code seed
                without an owner edit — the seed-if-absent gap that let a cap sit
                stale with no self-heal. Each is re-seedable in one click. */}
            {editorPlans.some((p) => p.drift.length > 0) && (
              <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/5 p-3" role="status">
                <p className="text-sm font-semibold text-foreground">
                  Plan drift — {editorPlans.reduce((n, p) => n + p.drift.length, 0)} value(s) differ from the code default and were not owner-edited
                </p>
                {editorPlans
                  .filter((p) => p.drift.length > 0)
                  .map((plan) => (
                    <div key={plan.slug} className="space-y-1">
                      {plan.drift.map((d) => (
                        <Cluster key={`${plan.slug}:${d.field}`} gap={2} className="text-xs">
                          <Badge tone="warning">{plan.name}</Badge>
                          <span className="font-mono text-foreground">{d.field}</span>
                          <span className="text-muted-foreground">
                            is <span className="font-mono text-foreground">{d.current}</span>, code default{' '}
                            <span className="font-mono text-foreground">{d.codeDefault}</span>
                          </span>
                          <RowActions
                            actions={[
                              {
                                id: 'reseed',
                                label: 'Re-seed to default',
                                variant: 'secondary',
                                disabled: planRevertMutation.isPending,
                                onClick: () => planRevertMutation.mutate({ slug: plan.slug, field: d.field }),
                              },
                            ]}
                          />
                        </Cluster>
                      ))}
                    </div>
                  ))}
              </div>
            )}
            {/* Owner-edited provenance (story 2-31 c): which fields are records
                that win over code defaults (never re-seeded), each revertable. */}
            {editorPlans.some((p) => p.ownerEditedFields.length > 0) && (
              <div className="space-y-1 rounded-lg border border-border/60 p-3">
                <p className="text-sm font-semibold text-foreground">Owner-edited fields — won’t be re-seeded on boot</p>
                {editorPlans
                  .filter((p) => p.ownerEditedFields.length > 0)
                  .map((plan) => (
                    <Cluster key={plan.slug} gap={2} className="text-xs">
                      <Badge tone="primary">{plan.name}</Badge>
                      {plan.ownerEditedFields.map((field) => (
                        <Inline
                          inline
                          gap={1}
                          key={field}
                          className="rounded border border-border/60 py-0.5 pl-2 pr-0.5"
                        >
                          <span className="font-mono text-foreground">{field}</span>
                          {/* Story 2-147xd: `RowActions` + `ActionDescriptor.hint`
                              (grown for exactly this shape — see ActionHosts.tsx's
                              own doc comment) — an always-shown explanatory
                              tooltip, unlike `disabledReason` which only shows
                              once disabled. */}
                          <RowActions
                            size="sm"
                            actions={[
                              {
                                id: `revert-${plan.slug}-${field}`,
                                label: 'Revert',
                                variant: 'ghost',
                                hint: `Revert ${field} to its code default (clears the owner-edited marker)`,
                                disabled: planRevertMutation.isPending,
                                onClick: () => planRevertMutation.mutate({ slug: plan.slug, field }),
                              },
                            ]}
                          />
                        </Inline>
                      ))}
                    </Cluster>
                  ))}
              </div>
            )}
            <Table aria-label="Plan limits">
              <THead>
                <TR>
                  <TH>Limit</TH>
                  {editorPlans.map((plan) => (
                    <TH key={plan.slug} className="whitespace-nowrap">
                      {plan.name}
                      {!plan.isPublic && (
                        <span className="ml-1 normal-case tracking-normal text-muted-foreground/70">(hidden)</span>
                      )}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                <TR>
                  <TD className="whitespace-nowrap font-medium">Price (USD / month)</TD>
                  {editorPlans.map((plan) => (
                    <TD key={plan.slug}>
                      <Input
                        aria-label={`${plan.name} monthly price`}
                        inputMode="decimal"
                        className="w-24 text-sm"
                        placeholder="—"
                        value={priceValue(plan)}
                        disabled={planMutation.isPending}
                        onChange={(e) => setDraft(draftKey(plan.slug, 'price'), e.target.value)}
                        onBlur={() => commitPrice(plan)}
                        onKeyDown={blurOnEnter}
                      />
                    </TD>
                  ))}
                </TR>
                {editorMetrics.map((metric) => (
                  <TR key={metric}>
                    <TD className="whitespace-nowrap font-mono text-xs">{metric}</TD>
                    {editorPlans.map((plan) => (
                      <TD key={plan.slug}>
                        <Input
                          aria-label={`${plan.name} ${metric} limit`}
                          inputMode="numeric"
                          className="w-24 text-sm"
                          placeholder="no cap"
                          value={limitValue(plan, metric)}
                          disabled={planMutation.isPending}
                          onChange={(e) => setDraft(draftKey(plan.slug, metric), e.target.value)}
                          onBlur={() => commitLimit(plan, metric)}
                          onKeyDown={blurOnEnter}
                        />
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Entitlements</p>
              <Table aria-label="Plan entitlements">
                <THead>
                  <TR>
                    <TH>Feature</TH>
                    {editorPlans.map((plan) => (
                      <TH key={plan.slug} className="whitespace-nowrap">
                        {plan.name}
                      </TH>
                    ))}
                  </TR>
                </THead>
                <TBody>
                  {(featuresQuery.data ?? []).map((feature) => (
                    <TR key={feature.key}>
                      <TD>
                        <p className="text-sm font-medium text-foreground">{feature.name}</p>
                        <p className="font-mono text-label text-muted-foreground">{feature.key}</p>
                      </TD>
                      {editorPlans.map((plan) => {
                        const entitled = plan.entitlements.includes(feature.key);
                        return (
                          <TD key={plan.slug}>
                            {/* Story 2-147xd: `RowActions` — `ariaLabel`
                                (disambiguating visible-text repeats across
                                rows, story 2-147xd) + `pressed` + `hint`
                                (always-shown tooltip) together cover what
                                this cell's raw `Button` used to hand-roll. */}
                            <RowActions
                              size="sm"
                              actions={[
                                {
                                  id: `${feature.key}-${plan.slug}`,
                                  label: entitled ? '✓' : '—',
                                  ariaLabel: `${feature.name} entitled on ${plan.name}`,
                                  variant: entitled ? 'primary' : 'ghost',
                                  pressed: entitled,
                                  hint: entitled ? 'Entitled — click to remove' : 'Not entitled — click to add',
                                  disabled: planMutation.isPending,
                                  onClick: () => toggleEntitlement(plan, feature.key),
                                },
                              ]}
                            />
                          </TD>
                        );
                      })}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </Section>

      {selectedOrgId && (
        <Surface as="section" variant="flat" padding="xl" className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Features — {detail?.name ?? '…'}</h2>
            <p className="text-sm text-muted-foreground">
              On enables a feature, Teaser shows it locked with an upsell, Off hides it. Granting a plan-locked
              feature requires a note.
            </p>
          </div>

          {/* Plan picker (story 2-22): assign/change this org's tier live. */}
          {detail && (
            <div className="space-y-2">
              <Cluster gap={3} className="rounded-lg border border-border/60 px-3 py-2">
                <p className="text-sm font-medium text-foreground">Plan</p>
                <Badge tone="neutral">{detail.plan}</Badge>
                <label className="sr-only" htmlFor="org-plan-assign">
                  Assign a plan to {detail.name}
                </label>
                <Select
                  id="org-plan-assign"
                  className="w-44 text-sm"
                  value={detail.plan}
                  disabled={assignPlanMutation.isPending}
                  onChange={(e) => {
                    const planSlug = e.target.value;
                    if (planSlug && planSlug !== detail.plan) assignPlanMutation.mutate({ orgId: detail.id, planSlug });
                  }}
                >
                  {editorPlans.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name} ({p.slug})
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  Entitlements and quotas re-resolve live; a downgrade shows lost features as teasers — no data is
                  touched.
                </p>
              </Cluster>
              {assignWarning && (
                <p className="rounded-lg bg-warning/[0.12] px-3 py-2 text-sm text-foreground" role="status">
                  {assignWarning}
                </p>
              )}
            </div>
          )}
          {!detail ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            groupedFeatures.map(([category, list]) => (
              <div key={category} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{category}</p>
                <Grid columns={{ base: 1, sm: 2 }} gap={2}>
                  {list.map((feature) => {
                    const current = stateFor(feature);
                    return (
                      <Cluster justify="between" gap={3} key={feature.key} className="rounded-lg border border-border/60 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">{feature.name}</p>
                          <p className="truncate font-mono text-label text-muted-foreground">{feature.key}</p>
                        </div>
                        <Inline gap={2}>
                          {!current.entitled && <Badge tone="warning">plan</Badge>}
                          {/* Story 2-31 (b): distinguish an explicit per-org override
                              (a flag row exists) from a value derived from plan/defaults. */}
                          {current.explicit ? (
                            <span title="An explicit per-org override is set — the value below is not derived from the plan/defaults">
                              <Badge tone="primary">override</Badge>
                            </span>
                          ) : (
                            <span title="Derived from the org's plan and feature defaults — no per-org override set">
                              <Badge tone="neutral">derived</Badge>
                            </span>
                          )}
                          {/* Story 2-147xd: `ToolbarActions` — its `role`/
                              `aria-label` passthrough was grown for exactly
                              this shape (see ActionHosts.tsx's own doc
                              comment, which names this call site directly).
                              `maxVisible` is the full state count so nothing
                              ever collapses into an overflow menu — this is
                              a closed, always-visible 3-state group. */}
                          <ToolbarActions
                            size="sm"
                            role="group"
                            aria-label={`${feature.name} state`}
                            maxVisible={FLAG_STATES.length}
                            actions={FLAG_STATES.map(
                              (option): ActionDescriptor => ({
                                id: option.value,
                                label: option.label,
                                variant: current.state === option.value ? 'primary' : 'ghost',
                                hint: option.title,
                                pressed: current.state === option.value,
                                disabled: flagMutation.isPending || clearFlagMutation.isPending,
                                onClick: () => setState(feature, option.value),
                              }),
                            )}
                          />
                          {/* Revert an override back to plan-derived (story 2-31 a). */}
                          <ToolbarActions
                            size="sm"
                            actions={[
                              {
                                id: 'revert',
                                label: 'Revert',
                                variant: 'ghost',
                                hint: "Clear the override — re-derive this feature from the org's plan/defaults",
                                disabled: !current.explicit || flagMutation.isPending || clearFlagMutation.isPending,
                                onClick: () => clearFlagMutation.mutate({ orgId: detail.id, featureKey: feature.key }),
                              },
                            ]}
                          />
                        </Inline>
                      </Cluster>
                    );
                  })}
                </Grid>
              </div>
            ))
          )}
        </Surface>
      )}

      {/* Policy inspector (story 2-51, P18 §11): "why can't Alice create a
          template?" — a dry-run EXPLAIN over the same resolver chain a real
          request takes. Own Surface section on this same single-scrolling
          page (matches the page's existing convention — org table, create-org
          modal, feature requests, per-org FlagMatrix all stack the same way);
          not its own tab/route since there is no precedent for one here. */}
      <Surface as="section" variant="flat" padding="xl" className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Policy inspector</h2>
          <p className="text-sm text-muted-foreground">
            Trace exactly why a principal would be allowed or denied a permission, feature, or quota — the
            same resolver functions the real request path calls, in order.
          </p>
        </div>

        <Form
          form={explainForm}
          id="policy-explain-form"
          className="space-y-4"
          onSubmit={(values) => {
            const query: Record<string, string> = { orgId: values.orgId.trim() };
            if (principalMode === 'userId' && values.userId) query.userId = values.userId.trim();
            if (principalMode === 'email' && values.email) query.email = values.email.trim();
            if (targetMode === 'capability' && values.capability) query.capability = values.capability.trim();
            if (targetMode === 'featureKey' && values.featureKey) query.featureKey = values.featureKey.trim();
            if (targetMode === 'metric' && values.metric) query.metric = values.metric;
            setError(null);
            explainMutation.mutate(query as Parameters<typeof explainPolicy>[0]);
          }}
        >
          <Grid columns={{ base: 1, sm: 3 }} gap={3}>
            <div className="space-y-1">
              <label className="text-label font-medium text-muted-foreground" htmlFor="policy-principal-mode">
                Identify principal by
              </label>
              <Select
                id="policy-principal-mode"
                className="w-full text-sm"
                value={principalMode}
                onChange={(e) => setPrincipalMode(e.target.value as 'userId' | 'email')}
              >
                <option value="email">Email</option>
                <option value="userId">User id</option>
              </Select>
            </div>
            {principalMode === 'email' ? (
              <FormField name="email" label="Principal email" required>
                {(field) => <Input type="email" className="w-full text-sm" placeholder="alice@acme.com" {...field} />}
              </FormField>
            ) : (
              <FormField name="userId" label="Principal user id" required>
                {(field) => <Input className="w-full text-sm" placeholder="24-hex ObjectId" {...field} />}
              </FormField>
            )}
            <FormField name="orgId" label="Organization id" required>
              {(field) => <Input className="w-full text-sm" placeholder="24-hex ObjectId" {...field} />}
            </FormField>
          </Grid>

          <Grid columns={{ base: 1, sm: 3 }} gap={3}>
            <div className="space-y-1">
              <label className="text-label font-medium text-muted-foreground" htmlFor="policy-target-mode">
                Evaluate
              </label>
              <Select
                id="policy-target-mode"
                className="w-full text-sm"
                value={targetMode}
                onChange={(e) => setTargetMode(e.target.value as 'capability' | 'featureKey' | 'metric')}
              >
                <option value="capability">Permission capability</option>
                <option value="featureKey">Feature key</option>
                <option value="metric">Quota metric</option>
              </Select>
            </div>
            {targetMode === 'capability' && (
              <FormField name="capability" label="Capability" required>
                {(field) => <Input className="w-full text-sm" placeholder="templates:create" {...field} />}
              </FormField>
            )}
            {targetMode === 'featureKey' && (
              <FormField name="featureKey" label="Feature key" required>
                {(field) => <Input className="w-full text-sm" placeholder="org.customRoles" {...field} />}
              </FormField>
            )}
            {targetMode === 'metric' && (
              <FormField name="metric" label="Metric" required>
                {(field) => (
                  <Select className="w-full text-sm" {...field}>
                    <option value="">Select a metric…</option>
                    {QUOTA_METRIC_OPTIONS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </Select>
                )}
              </FormField>
            )}
          </Grid>

          {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          <FormSubmit disabled={explainMutation.isPending}>
            {explainMutation.isPending ? 'Tracing…' : 'Explain'}
          </FormSubmit>
        </Form>

        {explainResult && (
          <div className="space-y-4 border-t border-border/60 pt-4">
            <Cluster gap={3} className="rounded-lg border border-border/60 px-3 py-2">
              <Badge tone={explainResult.allow ? 'success' : 'destructive'}>
                {explainResult.allow ? 'ALLOW' : 'DENY'}
              </Badge>
              <p className="text-sm text-foreground">{explainResult.reason}</p>
            </Cluster>
            <p className="text-xs text-muted-foreground">
              Principal {explainResult.principal.found ? 'resolved' : 'NOT FOUND'}
              {explainResult.principal.email ? ` — ${explainResult.principal.email}` : ''}
              {explainResult.principal.platformRole ? ` (platform ${explainResult.principal.platformRole})` : ''}
            </p>
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>Layer</TH>
                    <TH>Verdict</TH>
                    <TH>Deciding rule</TH>
                    <TH>Reason</TH>
                  </TR>
                </THead>
                <TBody>
                  {explainResult.trace.map((r: TraceRow) => (
                    <TR key={r.layer} className={!r.reached ? 'opacity-50' : undefined}>
                      <TD className="whitespace-nowrap text-sm font-medium text-foreground">{r.label}</TD>
                      <TD>
                        <Badge tone={TRACE_VERDICT_TONE[r.verdict]}>{r.verdict}</Badge>
                      </TD>
                      <TD className="font-mono text-label text-muted-foreground">{r.rule}</TD>
                      <TD className="text-sm text-muted-foreground">{r.reason ?? '—'}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </div>
        )}
      </Surface>

      {/* Story 2-27: owner-only assistant, pinned to the owner-plane platformOwnerAgent
          (1D-4 — internal, never auto-routed; owner gate enforced server-side via
          platformRole + policy.AGENT_PLATFORM_ROLE). Client-side `isOwner` gate here is
          UX only — mirrors the Sidebar's platformRole check (belt, not the buckle): the
          page itself already redirects non-owners before this renders. Panel-local
          mount, no shell edits — the org-plane assistant (AppShell) is untouched (still
          a distinct context, P18 §7 — story 2-133 just moved ITS OWN mount from a
          floating launcher to a shell-level drawer; this "Owner Assistant" mount keeps
          its original floating-launcher-and-card behavior, unmodified).
          `raised` (renamed from `dockOffsetPx` by story 2-147xd) no longer needed: it
          existed solely to stack this launcher above AppShell's own floating one so
          both were reachable at the same fixed bottom-right corner — 2-133 removed
          that floating launcher entirely (it's a drawer now, not a corner button), so
          there is nothing left for this one to stack above. Default (`false`) puts
          this back at the standard corner. */}
      {isOwner && (
        <AgentPanel
          agentName="platformOwnerAgent"
          context={{ route: 'platform' }}
          onArtifact={() => { /* platformOwnerAgent has no draft/artifact tools */ }}
          launcherLabel="Owner Assistant"
        />
      )}
    </AdministrationLayout>
  );
}
